import React, { useState } from 'react';
import {
  Box, TextField, Button, Select, MenuItem, IconButton,
  Typography, Snackbar, Alert, FormControl, InputLabel
} from '@mui/material';
import { AddCircleOutline, Delete } from '@mui/icons-material';
import ApiServices from '../../../Utils/ApiServices/ApiServices';
import { getDecryptedAccessToken } from '../../../Utils/LoginUserDetails.js/LoginUserSavedDetails';

const questionTypes = [
  { value: 'text', label: 'Text' },
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multi_choice', label: 'Multi Choice' }
];

const DietPlanQuestionnaire = () => {
  const accessToken = getDecryptedAccessToken();
 const [nextId, setNextId] = useState(1);
  const [questions, setQuestions] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [jsonInput, setJsonInput] = useState('');

const handleJsonParse = () => {
  try {
    const parsed = JSON.parse(jsonInput);
    if (
      parsed?.content?.questionnaire &&
      Array.isArray(parsed.content.questionnaire)
    ) {
      const mappedQuestions = parsed.content.questionnaire.map((q, index) => ({
        id: index + 1,
        question: q.question || '',
        type: q.type || 'text',
        placeholder: q.placeholder || '',
        options: q.options || [],
      }));
      setQuestions(mappedQuestions);
      setNextId(mappedQuestions.length + 1);
      setSnackbar({ open: true, message: 'JSON parsed successfully', severity: 'success' });
    } else {
      throw new Error('Invalid structure');
    }
  } catch (err) {
    setSnackbar({ open: true, message: 'Invalid JSON format or structure', severity: 'error' });
  }
};


  const handleAddQuestion = () => {
    const newQuestion = {
      id: nextId,
      question: '',
      type: 'text',
      placeholder: '',
      options: []
    };
    setQuestions([...questions, newQuestion]);
    setNextId(prev => prev + 1);
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;

    // Clear options for non-choice types
    if (field === 'type' && value === 'text') {
      updated[index].options = [];
    }

    setQuestions(updated);
  };

  const handleOptionChange = (qIndex, oIndex, value) => {
    const updated = [...questions];
    updated[qIndex].options[oIndex] = value;
    setQuestions(updated);
  };

  const handleAddOption = (qIndex) => {
    const updated = [...questions];
    updated[qIndex].options.push('');
    setQuestions(updated);
  };

  const handleRemoveOption = (qIndex, oIndex) => {
    const updated = [...questions];
    updated[qIndex].options.splice(oIndex, 1);
    setQuestions(updated);
  };

  const handleDeleteQuestion = (index) => {
    const updated = [...questions];
    updated.splice(index, 1);
    setQuestions(updated);
     const reassigned = updated.map((q, i) => ({
      ...q,
      id: i + 1
    }));
    setQuestions(reassigned);
    setNextId(reassigned.length + 1);   
  };

  const handleSubmit = async () => {
    if (questions.length === 0) {
      setSnackbar({ open: true, message: 'Please add at least one question', severity: 'warning' });
      return;
    }

    const isValid = questions.every(q => q.question.trim() !== '' && (q.type === 'text' || q.options.length > 0));
    if (!isValid) {
      setSnackbar({ open: true, message: 'Fill in all questions and options properly', severity: 'warning' });
      return;
    }

    const payload = {
      title: "Health & Nutrition Questionnaire",
      version: "1.0",
      content: {
        questionnaire: questions.map(({ id, question, type, placeholder, options }) => ({
          id,
          question,
          type,
          ...(type === 'text' && { placeholder }),
          ...(type !== 'text' && { options })
        }))
      }
    };

    try {
      setLoading(true);
      await ApiServices.post('/apis/default/api/dietplanquestionnaire', payload, {
        Authorization: `Bearer ${accessToken}`,
      });
      setSnackbar({ open: true, message: 'Questionnaire submitted successfully!', severity: 'success' });
      setQuestions([]);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to submit questionnaire', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h5" mb={2}>Create Diet Plan Questionnaire</Typography>

      <Box mb={4}>
  <Typography variant="h6" mb={1}>Paste Questionnaire JSON</Typography>
  <TextField
    multiline
    rows={6}
    fullWidth
    placeholder='Paste JSON here...'
    value={jsonInput}
    onChange={(e) => setJsonInput(e.target.value)}
  />
  <Box mt={1}>
    <Button variant="outlined" onClick={handleJsonParse}>
      Parse JSON to Form
    </Button>
  </Box>
</Box>


      {questions.map((q, index) => (
        <Box key={q.id} border="1px solid #ccc" borderRadius={2} p={2} mb={2}>
          <TextField
            label="Question"
            fullWidth
            margin="normal"
            value={q.question}
            onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Question Type</InputLabel>
            <Select
              value={q.type}
              onChange={(e) => handleQuestionChange(index, 'type', e.target.value)}
              label="Question Type"
            >
              {questionTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {q.type === 'text' && (
            <TextField
              label="Placeholder"
              fullWidth
              margin="normal"
              value={q.placeholder}
              onChange={(e) => handleQuestionChange(index, 'placeholder', e.target.value)}
            />
          )}

          {(q.type === 'single_choice' || q.type === 'multi_choice') && (
            <Box>
              <Typography variant="subtitle2" mt={2}>Options:</Typography>
              {q.options.map((opt, oIndex) => (
                <Box key={oIndex} display="flex" alignItems="center" mb={1}>
                  <TextField
                    label={`Option ${oIndex + 1}`}
                    value={opt}
                    onChange={(e) => handleOptionChange(index, oIndex, e.target.value)}
                    fullWidth
                  />
                  <IconButton color="error" onClick={() => handleRemoveOption(index, oIndex)}>
                    <Delete />
                  </IconButton>
                </Box>
              ))}
              <Button variant="outlined" size="small" onClick={() => handleAddOption(index)} startIcon={<AddCircleOutline />}>
                Add Option
              </Button>
            </Box>
          )}

          <Box mt={2}>
            <Button color="error" onClick={() => handleDeleteQuestion(index)}>
              Delete Question
            </Button>
          </Box>
        </Box>
      ))}

      <Box mt={2}>
        <Button variant="contained" onClick={handleAddQuestion} startIcon={<AddCircleOutline />}>
          Add Question
        </Button>
      </Box>

      <Box mt={3}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Submit Questionnaire'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DietPlanQuestionnaire;
